import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Fade,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  FormLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DescriptionIcon from "@material-ui/icons/Description";
import axios from "axios";
import { toast } from "react-toastify";
import { decryptData } from "../../../../crypto";
import useStyles from "../../../../styles";
import Loader from "./Loader";

const ImportUidPopup = ({ open, handleClose, selectedRowData, fetchData }) => {
  const classes = useStyles();
  const decryptedToken = decryptData(sessionStorage.getItem("token"));
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/json" && !file.name.endsWith(".json")) {
        toast.warn("Please select a valid JSON file (.json)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImportButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      toast.warn("Please select a JSON file to import.");
      return;
    }

    setLoading(true);
    try {
      // const formData = new FormData();
      // formData.append("file", selectedFile);
      // if (selectedRowData?.id) {
      //   formData.append("exam_id", selectedRowData.id);
      // }
      const fileText = await selectedFile.text();
      const jsonData = JSON.parse(fileText);

      const endpoint = `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/question/add/import/uid/json/${selectedRowData?.id}`;

      const response = await axios.post(endpoint, jsonData, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 || response.data?.success) {
        toast.success(response.data?.message || "UID imported successfully!");
        if (typeof fetchData === "function") {
          fetchData();
        }
        handleClose();
      } else {
        toast.error(response.data?.message || "Failed to import UID.");
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" ? error.response.data : null) ||
        "Error importing UID file.";
      toast.error(errMsg);
      console.error("Import UID Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader message="Importing UID JSON, please wait..." />}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          style: { borderRadius: "12px", padding: "16px" },
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <DialogTitle style={{ padding: "8px 24px 8px 8px" }}>
            <Typography variant="h6" className={classes.textcolorformhead} style={{ fontWeight: 600 }}>
              Import UID
            </Typography>
          </DialogTitle>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>

        <DialogContent dividers>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0" }}>
            {selectedRowData?.exam_name && (
              <div>
                <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                  Exam: <span style={{ color: "#333", fontWeight: 600 }}>{selectedRowData.exam_name}</span>
                </Typography>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <FormLabel className={classes.textcolorformlabel}>
                Select JSON File <span className={classes.textcolorred}>*</span>
              </FormLabel>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json,application/json"
                style={{ display: "none" }}
              />

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleImportButtonClick}
                  className={classes.custombtnoutline}
                >
                  Import File
                </Button>

                {selectedFile && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: "#f5f5f5",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <DescriptionIcon style={{ fontSize: 18, color: "#1976d2" }} />
                    <Typography variant="body2" style={{ fontWeight: 500, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {selectedFile.name}
                    </Typography>
                    <IconButton size="small" onClick={handleRemoveFile} style={{ padding: 2 }}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                )}
              </div>
              {/* <Typography variant="caption" color="textSecondary">
                Allowed file format: .json
              </Typography> */}
            </div>

            <div
              className={`${classes.dflex} ${classes.justifyflexend}`}
              style={{ marginTop: "8px" }}
            >
              <Button
                className={classes.custombtnoutline}
                onClick={handleClose}
                style={{ marginRight: "8px" }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                className={classes.custombtnblue}
                onClick={handleSubmit}
                disabled={loading || !selectedFile}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportUidPopup;
